/** Road-matched routes, underground segments and observed tunnel portals. */
import { BUS_ROUTE, CAR_ROUTE, ROUTE_PORTALS } from '../data/route.js';
import ELEVATION from '../data/route-elevation.json';
import { createRouteSampler } from './route-sampler.js';
import { createTunnelPortalLayer } from './tunnel-portals.js';

/**
 * Oppslag langs hver rute: posisjon, terrenghøyde og structure på en gitt
 * avstand fra start. Høydene er bakt ut av terrengflisene, så de er klare
 * med en gang – ingen venting på at DEM-fliser skal lastes.
 *
 *   const p = ROUTE_SAMPLERS.bus.at(4200);
 *   marker.setLngLat(p.lngLat, p.elevation);
 *   marker.setHidden(p.structure === 'tunnel');
 */
export const ROUTE_SAMPLERS = {
  bus: createRouteSampler(BUS_ROUTE, ELEVATION.bus),
  car: createRouteSampler(CAR_ROUTE, ELEVATION.car),
};

/**
 * Rutefarger. Objekt og rute deler verdi, så «gul linje → gul bil» leses
 * uten legend. Kanten er ADAPTIV og settes av paletten: gul har høy
 * luminans og trenger mørk kant, grønn er mørk og trenger lys kant. Uten
 * det forsvinner én av dem over skog, vei, vann eller bygninger.
 *
 * Muteres av theme.js ved palettbytte – ikke bytt den ut, skriv i den.
 */
export const ROUTE_COLORS = {
  bus: { line: '#34634a', casing: '#ffffff', casingOpacity: .9 },
  car: { line: '#ffcc33', casing: '#232528', casingOpacity: .8 },
};

// Kanten er en MULTIPLIKATOR av linjebredden, ikke et fast pikseltillegg.
// Et fast tillegg blir 3x linja på zoom 11 og 1,5x på zoom 20, og da ser
// ruta «ekstra markert» ut på vidvinkel og normal tett på.
const CASING_RATIO = 1.75;

// Keep the trace within the road instead of covering neighboring properties.
// Yellow is slightly wider, so shared stretches show both modes without moving
// either geometry off its road. Widths are display pixels, never geographic offsets.
function routeWidth(mode, mult = 1) {
  const k = (mode === 'bus' ? .85 : 1) * mult;
  const road = (major, primary, minor) => ['match', ['get', 'road_class'],
    ['motorway', 'trunk'], major * k,
    ['primary', 'secondary', 'tertiary'], primary * k,
    minor * k];
  return ['interpolate', ['exponential', 1.4], ['zoom'],
    11, road(4, 3.4, 3), 16, road(9, 7, 5.5), 20, road(22, 15, 11)];
}

/** Rekkefølgen lagene legges i. Gangstrekket sist, øverst. */
export const ROUTE_STRUCTURES = ['tunnel', 'surface', 'bridge', 'walk'];

export function routeLayers(mode, structure) {
  const id = `route-${mode}`, colors = ROUTE_COLORS[mode];
  const tunnel = structure === 'tunnel';
  const filter = ['all', ['==', ['get', 'match_status'], 'matched'], ['==', ['get', 'structure'], structure]];
  const line = {
    id: `${id}-${structure}-line`, type: 'line', source: id, filter,
    // Samme stableorden som basiskartets veier (se byLayer i layers.js):
    // der ruta krysser seg selv i to plan skal det øverste tegnes øverst.
    layout: { 'line-cap': tunnel ? 'butt' : 'round', 'line-join': 'round',
      'line-sort-key': ['coalesce', ['get', 'layer'], 0] },
    paint: { 'line-color': colors.line, 'line-width': routeWidth(mode), 'line-opacity': tunnel ? .65 : 1,
      ...(tunnel ? { 'line-dasharray': [2, 2] } : {}) },
  };
  // Gangstrekket: stiplet, bygget som TUNNELEN - som beviselig rendres.
  //
  // Første forsøk brukte `line-dasharray: [0, 2]` med runde linjeender for å
  // få prikker, egen bredde og egen kant. Ingenting ble tegnet. Her avvikes
  // det derfor minst mulig fra oppskriften som virker: rette linjeender,
  // ingen null i mønsteret, uendret bredde, ingen kant.
  //
  // Tettere streker enn tunnelens [2, 2], så de to ikke forveksles: begge
  // betyr «her kjører ikke bussen», men av helt ulike grunner.
  if (structure === 'walk') {
    return [{ ...line,
      layout: { ...line.layout, 'line-cap': 'butt' },
      paint: { ...line.paint, 'line-dasharray': [1.4, 1.2] },
    }];
  }

  if (tunnel) return [line]; // Ingen kant som ville tettet igjen gapene under bakken.

  // Ingen kant/casing – ren farget linje (grønn buss, gul bil).
  return [line];
}

/**
 * Skjuler eller viser en hel rute (alle lag for modusen).
 *
 * Brukes av introen, som bare skal etablere bussreisen. Vi bytter
 * `visibility` i stedet for å tømme kilden: da kan den slås på igjen fra
 * avkrysningsboksen uten at noe må lastes på nytt, og uten at rutas
 * opptegningstilstand går tapt.
 */
export function setRouteVisibility(map, mode, visible) {
  const box = document.querySelector(`#show-route-${mode}`);
  if (box) box.checked = visible;
  for (const structure of ROUTE_STRUCTURES) {
    for (const suffix of ['casing', 'line']) {
      const id = `route-${mode}-${structure}-${suffix}`;
      if (map.getLayer(id)) {
        map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
      }
    }
  }
}

export function setTunnelVisibility(map, visible) {
  for (const mode of ['car', 'bus']) {
    const id = `route-${mode}-tunnel-line`;
    if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
  }
}

export function addRoutes(map, beforeId, attribution = 'Ruter/Entur (NLOD) · © OpenStreetMap contributors') {
  for (const [mode, data] of [['car', CAR_ROUTE], ['bus', BUS_ROUTE]]) {
    map.addSource(`route-${mode}`, {
      type: 'geojson', data, tolerance: 0, maxzoom: 22,
      attribution,
      // lineMetrics is intentionally off: it alters dash scaling for GeoJSON.
    });
  }
  const add = layer => map.addLayer(layer, beforeId);
  for (const structure of ['tunnel', 'surface']) for (const mode of ['car', 'bus']) routeLayers(mode, structure).forEach(add);

  // Redraw mapped bridges above surface routes. Thus a surface trace passing
  // underneath a bridge is occluded by the actual bridge deck, not mislabelled
  // as a long tunnel. Bridge route segments themselves are drawn on top next.
  for (const layer of map.getStyle().layers.filter(l => l.type === 'line' && l['source-layer'] === 'transportation' && (l.id.startsWith('road-') || l.id === 'rail'))) {
    add({ ...layer, id: `route-bridge-cover-${layer.id}`, filter: ['all', layer.filter ?? true, ['==', ['get', 'brunnel'], 'bridge']] });
  }
  for (const mode of ['car', 'bus']) routeLayers(mode, 'bridge').forEach(add);
  for (const mode of ['car', 'bus']) routeLayers(mode, 'walk').forEach(add);
  // Tunnelmarkering er ÉN ting: den grå buen, og bare tett på
  // (PORTAL_MIN_ZOOM). De gamle ikonene med «Tunnel inn»/«Tunnel ut» er
  // borte - de dukket opp på mellomnivåer og rotet til vidvinkelbildet.
  // Lenger ute er den stiplede ruta markering nok, og den kan slås av.
  add(createTunnelPortalLayer(ROUTE_PORTALS));

  const control = document.querySelector('#show-tunnels');
  if (control) {
    setTunnelVisibility(map, control.checked);
    control.addEventListener('change', () => setTunnelVisibility(map, control.checked));
  }
  for (const mode of ['car', 'bus']) {
    const box = document.querySelector(`#show-route-${mode}`);
    if (!box) continue;
    box.addEventListener('change', () => setRouteVisibility(map, mode, box.checked));
  }
}
